import React, { useState, useEffect } from 'react';
import { Share, ChevronDown, HelpCircle } from 'lucide-react';

const formatCurrency = (amount) => {
  // Convert number to string and handle decimals
  const numStr = Math.abs(amount).toFixed(0);
  
  // Split integer into parts based on Indian numbering system
  const lastThree = numStr.substring(numStr.length - 3);
  const otherNums = numStr.substring(0, numStr.length - 3);
  
  // Add commas according to Indian number system
  const formatted = otherNums !== '' 
    ? otherNums.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + lastThree 
    : lastThree;
  
  return '₹' + (amount < 0 ? '-' : '') + formatted;
};

const EducationLoanCalculator = () => {
  const [values, setValues] = useState({
    loanAmount: 1000000,
    loanTenure: 6,
    interestRate: 8,
    repaymentYears: 1,
    moratoriumPeriod: 1 // Course duration plus 6 months to 1 year
  });

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showShareToast, setShowShareToast] = useState(false);
  const [showToolTip, setShowToolTip] = useState('');
  
  const handleChange = (key, value) => {
    setValues(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const calculateEMI = () => {
    const { loanAmount, loanTenure, interestRate, repaymentYears, moratoriumPeriod } = values;
    
    // Monthly interest rate
    const monthlyRate = interestRate / (12 * 100);
    
    // Total number of monthly payments
    const totalMonths = loanTenure * 12;
    const repaidMonths = repaymentYears * 12;
    const moratoriumMonths = moratoriumPeriod * 12;
    
    // Interest accumulated during moratorium
    const moratoriumInterest = loanAmount * monthlyRate * moratoriumMonths;
    const totalPrincipal = loanAmount + moratoriumInterest;
    
    // Calculate EMI
    const numerator = totalPrincipal * monthlyRate * Math.pow(1 + monthlyRate, totalMonths);
    const denominator = Math.pow(1 + monthlyRate, totalMonths) - 1;
    const emi = numerator / denominator;
    
    // Total amount payable
    const totalAmount = emi * totalMonths;
    
    // Monthly principal = Total loan amount / Total months
    const monthlyPrincipal = totalPrincipal / totalMonths;
    
    // Calculate repayment details
    const principleRepaid = monthlyPrincipal * repaidMonths;
    const interestPaid = (emi - monthlyPrincipal) * repaidMonths;
    const outstandingLoan = totalAmount - (emi * repaidMonths);
    
    return {
      emi: Math.round(emi),
      totalAmount: Math.round(totalAmount),
      outstandingAmount: Math.round(outstandingLoan),
      principleRepaid: Math.round(principleRepaid),
      interestPaid: Math.round(interestPaid),
      moratoriumInterest: Math.round(moratoriumInterest)
    };
  };

  const [result, setResult] = useState(calculateEMI());

  useEffect(() => {
    setResult(calculateEMI());
  }, [values]);

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setShowShareToast(true);
      setTimeout(() => setShowShareToast(false), 3000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  return (
    <div className="calculator-container pt-24">
      <div className="calculator-header text-center mb-8">
        <h1 className="text-2xl font-semibold text-[#113262] mb-2">Education Loan EMI Calculator</h1>
        <h2 className="text-lg text-gray-600">Calculate your education loan EMI with moratorium period</h2>
      </div>

      <div className="max-w-5xl mx-auto p-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Input Sections */}
          <div className="lg:col-span-2 space-y-8">
            {/* Loan Amount */}
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Loan Details</h2>
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block font-medium text-sm mb-1.5 flex items-center">
                    Education Loan Amount
                    <div className="relative ml-1">
                      <HelpCircle 
                        size={14} 
                        className="text-gray-400 cursor-pointer"
                        onMouseEnter={() => setShowToolTip('loanAmount')}
                        onMouseLeave={() => setShowToolTip('')}
                      />
                      {showToolTip === 'loanAmount' && (
                        <div className="absolute z-10 left-6 -top-2 w-60 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                          Principal amount of your education loan
                        </div>
                      )}
                    </div>
                  </label>
                  <input
                    type="number"
                    value={values.loanAmount}
                    onChange={(e) => handleChange('loanAmount', Number(e.target.value))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={100000}
                    max={10000000}
                    step={10000}
                    value={values.loanAmount}
                    onChange={(e) => handleChange('loanAmount', Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>₹1L</span>
                    <span>₹1Cr</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Interest & Tenure */}
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Interest & Tenure</h2>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block font-medium text-sm mb-1.5 flex items-center">
                    Interest Rate
                    <div className="relative ml-1">
                      <HelpCircle 
                        size={14} 
                        className="text-gray-400 cursor-pointer"
                        onMouseEnter={() => setShowToolTip('interestRate')}
                        onMouseLeave={() => setShowToolTip('')}
                      />
                      {showToolTip === 'interestRate' && (
                        <div className="absolute z-10 left-6 -top-2 w-60 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                          Annual interest rate for your education loan
                        </div>
                      )}
                    </div>
                  </label>
                  <input
                    type="number"
                    value={values.interestRate}
                    onChange={(e) => handleChange('interestRate', Number(e.target.value))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={6}
                    max={16}
                    step={0.1}
                    value={values.interestRate}
                    onChange={(e) => handleChange('interestRate', Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>6%</span>
                    <span>16%</span>
                  </div>
                </div>

                <div>
                  <label className="block font-medium text-sm mb-1.5">Loan Tenure (Years)</label>
                  <input
                    type="number"
                    value={values.loanTenure}
                    onChange={(e) => handleChange('loanTenure', Number(e.target.value))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={1}
                    max={15}
                    step={1}
                    value={values.loanTenure}
                    onChange={(e) => handleChange('loanTenure', Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>1 year</span>
                    <span>15 years</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Moratorium Period */}
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Moratorium Period</h2>
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block font-medium text-sm mb-1.5 flex items-center">
                    Moratorium Period
                    <div className="relative ml-1">
                      <HelpCircle 
                        size={14} 
                        className="text-gray-400 cursor-pointer"
                        onMouseEnter={() => setShowToolTip('moratoriumPeriod')}
                        onMouseLeave={() => setShowToolTip('')}
                      />
                      {showToolTip === 'moratoriumPeriod' && (
                        <div className="absolute z-10 left-6 -top-2 w-72 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                          Period during which you don't have to pay EMIs (typically course duration plus 6-12 months)
                        </div>
                      )}
                    </div>
                  </label>
                  <input
                    type="number"
                    value={values.moratoriumPeriod}
                    onChange={(e) => handleChange('moratoriumPeriod', Number(e.target.value))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={0}
                    max={5}
                    step={0.5}
                    value={values.moratoriumPeriod}
                    onChange={(e) => handleChange('moratoriumPeriod', Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>0 years</span>
                    <span>5 years</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Advanced Settings */}
            <div className="bg-white rounded-lg shadow">
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="w-full p-4 flex justify-between items-center hover:bg-gray-50"
              >
                <h2 className="text-lg font-bold text-gray-900">Advanced Settings</h2>
                <ChevronDown 
                  className={`transform transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
                  size={20}
                />
              </button>
              
              {showAdvanced && (
                <div className="p-4 border-t">
                  <div className="grid grid-cols-1 gap-6">
                    <div>
                      <label className="block font-medium text-sm mb-1.5 flex items-center">
                        Repayment Years
                        <div className="relative ml-1">
                          <HelpCircle 
                            size={14} 
                            className="text-gray-400 cursor-pointer"
                            onMouseEnter={() => setShowToolTip('repaymentYears')}
                            onMouseLeave={() => setShowToolTip('')}
                          />
                          {showToolTip === 'repaymentYears' && (
                            <div className="absolute z-10 left-6 -top-2 w-60 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                              Years of loan repayment completed
                            </div>
                          )}
                        </div>
                      </label>
                      <input
                        type="number"
                        value={values.repaymentYears}
                        onChange={(e) => handleChange('repaymentYears', Math.min(values.loanTenure, Number(e.target.value)))}
                        className="w-full p-1.5 border rounded text-sm"
                      />
                      <input
                        type="range"
                        min={0}
                        max={values.loanTenure}
                        step={1}
                        value={values.repaymentYears}
                        onChange={(e) => handleChange('repaymentYears', Number(e.target.value))}
                        className="w-full mt-2"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>0 years</span>
                        <span>{values.loanTenure} years</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Results Section */}
          <div className="bg-[#113262] text-white rounded-lg h-[500px] sticky top-6">
            <div className="p-4 border-b border-white/20">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold">Education Loan EMI</h3>
                <button onClick={handleShare} className="p-1 hover:bg-blue-700 rounded">
                  <Share size={18} />
                </button>
              </div>
            </div>

            <div className="p-4 flex flex-col h-[calc(100%-68px)] justify-between">
              <div className="space-y-4">
                <div className="text-center">
                  <div className="text-2xl font-bold mb-1">
                    {formatCurrency(result.emi)}
                  </div>
                  <div className="text-sm text-gray-300">Monthly EMI after moratorium</div>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Total Loan Amount</span>
                    <span className="font-bold">{formatCurrency(result.totalAmount)}</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Outstanding Amount</span>
                    <span className="font-bold">{formatCurrency(result.outstandingAmount)}</span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Principle Repaid</span>
                    <span className="font-bold">{formatCurrency(result.principleRepaid)}</span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Interest Paid</span>
                    <span className="font-bold">{formatCurrency(result.interestPaid)}</span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Moratorium Interest</span>
                    <span className="font-bold">{formatCurrency(result.moratoriumInterest)}</span>
                  </div>
                </div>
              </div>

              <button className="w-full bg-orange-400 text-white py-2 rounded-lg mt-4 hover:bg-orange-500 transition-colors text-sm">
                Apply for Education Loan →
              </button>
            </div>
          </div>

          {showShareToast && (
            <div className="fixed top-4 right-4 bg-green-600 text-white px-6 py-3 rounded shadow-lg">
              Link copied!
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EducationLoanCalculator;